import { ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/shared/empty-state';
import { Button } from '@/components/ui/button';

interface ListErrorStateProps {
  /** 401/403 → általános „nincs jogosultságod" állapot (retry nélkül, mert értelmetlen). */
  forbidden: boolean;
  /** Egyéb (hálózat/szerver) hiba esetén a lekérdezés újrafuttatása — ekkor jelenik meg az „Újra" gomb. */
  onRetry?: () => void;
  /**
   * A hiba-állapot SZÖVEG-felülírása. MIÉRT KELL: a belső felület TEGEZ („Próbáld újra"), a partner-portál
   * viszont végig ÖNÖZ — ugyanaz a komponens két hangnemben szolgál. A default a belső (meglévő) szöveg, a
   * partner-oldali hívók a `PortalErrorState` wrapperen át adják a saját feliratukat.
   */
  title?: string;
  description?: string;
}

/**
 * Egységes lista-hiba állapot — KÉT szétválasztott esettel, végfelhasználó-barát szöveggel (NINCS belső
 * jog-kulcs / e-mail / belső instrukció szivárgás). `forbidden` → jog-hiba üzenet (gomb nélkül); egyébként
 * → általános betöltési hiba + „Újra" gomb (ha van `onRetry`). Az `EmptyState` atomra épül, minden szöveg i18n-ből.
 */
export function ListErrorState({
  forbidden,
  onRetry,
  title,
  description,
}: ListErrorStateProps): JSX.Element {
  const { t } = useTranslation();

  if (forbidden) {
    return (
      <EmptyState
        icon={LockClosedIcon}
        title={t('common.noAccessTitle')}
        description={t('common.noAccessDesc')}
      />
    );
  }

  return (
    <EmptyState
      icon={ExclamationTriangleIcon}
      title={title ?? t('common.loadErrorTitle')}
      description={description ?? t('common.loadErrorDesc')}
      action={
        onRetry ? (
          <Button variant="outline" onClick={onRetry} aria-label={t('common.retry')}>
            {t('common.retry')}
          </Button>
        ) : undefined
      }
    />
  );
}
